export function formatNetworkCurrency(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    const millions = (abs / 1_000_000).toFixed(2).replace(/\.?0+$/, "");
    return `${value < 0 ? "−" : ""}$${millions}M`;
  }
  if (abs >= 1_000) {
    return `${value < 0 ? "−" : ""}$${(abs / 1_000).toFixed(0)}K`;
  }
  return `${value < 0 ? "−" : ""}$${abs.toFixed(0)}`;
}

/** CMS star rating for network summary cards (trims trailing zeros). */
export function formatNetworkRating(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  const rounded = Math.round(value * 100) / 100;
  return rounded.toFixed(2).replace(/\.?0+$/, "");
}

/** Overall-rating delta vs. state average (e.g. −1.0, +0.5). */
export function formatVsStateDelta(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  const rounded = Math.round(value * 10) / 10;
  const str = rounded.toFixed(1).replace(/\.0$/, "");
  if (rounded > 0) return `+${str}`;
  return str;
}

/**
 * CMS quality measure score for network heatmap cells.
 * Decimal rates (0.166) → percent; values already in percent scale keep one decimal.
 */
export function formatQualityMeasureScore(score: number | null | undefined): string {
  if (score == null || Number.isNaN(score)) return "—";
  const abs = Math.abs(score);
  if (abs > 0 && abs < 1) {
    const pct = Math.round(score * 1000) / 10;
    const str = pct.toFixed(1).replace(/\.0$/, "");
    return `${str}%`;
  }
  if (abs <= 100) {
    const pct = Math.round(score * 10) / 10;
    const str = pct.toFixed(1).replace(/\.0$/, "");
    return `${str}%`;
  }
  const rounded = Math.round(score * 10) / 10;
  return rounded.toFixed(1).replace(/\.0$/, "");
}

/** Short label for quality measure column headers. */
export function abbreviateQualityMeasureLabel(label: string | null | undefined): string {
  const t = (label || "").trim();
  if (!t) return "—";
  if (t.length <= 16) return t;
  const words = t.split(/\s+/);
  if (words.length >= 2) {
    const short = words.slice(0, 2).join(" ");
    return short.length <= 16 ? short : `${short.slice(0, 15)}…`;
  }
  return `${t.slice(0, 15)}…`;
}

export function formatOccupancy(rate: number | null | undefined): string {
  if (rate == null) return "—";
  return `${(rate * 100).toFixed(1)}% occ`;
}

/** True when a value is safe to show as a CMS CCN on the Network portfolio subtitle. */
export function isNetworkPortfolioCcn(value: string): boolean {
  const t = value.trim();
  if (!t || t.startsWith("FAC-") || t.includes("_")) return false;
  if (/^\d{10}$/.test(t)) return false;
  if (/^\d{1,6}$/.test(t)) return true;
  return /^[0-9A-Za-z]{6,12}$/.test(t);
}

/** Normalize a CMS CCN for display (zero-pad numeric CCNs to 6 digits). */
export function normalizeNetworkPortfolioCcn(value: string): string {
  const t = value.trim();
  if (/^\d{1,6}$/.test(t)) return t.padStart(6, "0");
  return t;
}

/**
 * Resolve displayable CCNs from API meta and/or the selected facility row.
 * Meta ``ccns`` are CMS-validated on the server; the row may still hold FAC- / NPI ids.
 */
export function resolveNetworkPortfolioCcns(
  metaCcns: string[] | undefined,
  facilityCcnNumber?: string | null,
): string[] {
  const fromMeta = (metaCcns ?? [])
    .map((c) => normalizeNetworkPortfolioCcn(c))
    .filter((c) => isNetworkPortfolioCcn(c));
  if (fromMeta.length) {
    return [...new Set(fromMeta)];
  }
  const raw = (facilityCcnNumber ?? "").trim();
  if (!raw) return [];
  if (isNetworkPortfolioCcn(raw)) {
    return [normalizeNetworkPortfolioCcn(raw)];
  }
  const suffix = raw.match(/_(\d{5,6})$/);
  if (suffix) {
    const padded = normalizeNetworkPortfolioCcn(suffix[1]);
    if (isNetworkPortfolioCcn(padded)) return [padded];
  }
  return [];
}

/** Suffix for the Network tab subtitle, e.g. `` · CCN 505418`` or `` · CCNs 505418, 015009``. */
export function formatNetworkPortfolioCcnSuffix(
  metaCcns: string[] | undefined,
  facilityCcnNumber?: string | null,
): string {
  const ccns = resolveNetworkPortfolioCcns(metaCcns, facilityCcnNumber);
  if (!ccns.length) return "";
  if (ccns.length === 1) return ` · CCN ${ccns[0]}`;
  return ` · CCNs ${ccns.join(", ")}`;
}

/** CMS ``as_of_date`` (ISO or parseable) → ``March 1, 2021``. */
export function formatNetworkPortfolioPeriodEnding(
  asOfDate: string | null | undefined,
): string | null {
  if (!asOfDate || !String(asOfDate).trim()) return null;
  const trimmed = String(asOfDate).trim();
  const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const dt = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    if (!Number.isNaN(dt.getTime())) {
      return dt.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }
  }
  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }
  return trimmed;
}

/**
 * Network tab portfolio subtitle (replaces per-facility name / CCN list line).
 * Example: Portfolio-wide compliance, quality, and financial-performance view across all 2
 * facilities for period ending March 1, 2021
 */
/** Normalize and sort two-letter US state codes for network portfolio display. */
export function normalizeNetworkPortfolioStates(states: Iterable<string> | null | undefined): string[] {
  if (!states) return [];
  const out = new Set<string>();
  for (const raw of states) {
    const st = String(raw || "")
      .trim()
      .toUpperCase();
    if (/^[A-Z]{2}$/.test(st)) out.add(st);
  }
  return [...out].sort();
}

/** e.g. ``WA · OR · ID · CA`` */
export function formatNetworkStateList(states: Iterable<string> | null | undefined): string {
  return normalizeNetworkPortfolioStates(states).join(" · ");
}

/** Collect states from network-dashboard meta CCN location map. */
export function collectPortfolioStatesFromMeta(
  meta: { ccn_facility_locations?: Record<string, { state?: string | null }> } | null | undefined,
): string[] {
  const locs = meta?.ccn_facility_locations;
  if (!locs) return [];
  const states: string[] = [];
  for (const loc of Object.values(locs)) {
    const st = (loc?.state || "").trim();
    if (st) states.push(st);
  }
  return normalizeNetworkPortfolioStates(states);
}

/**
 * Selected-network detail line (mockup): ``32 facilities · WA · OR · ID · CA — Chain ID 04428``
 */
export function formatNetworkPortfolioSelectorDetail(options: {
  facilityCount: number;
  states?: Iterable<string> | null;
  chainId?: string | number | null;
  /** Internal Unicomply network PK — distinguishes copies that share a CMS Chain ID. */
  networkId?: string | number | null;
}): string {
  const count = Math.max(0, options.facilityCount);
  const facilityWord = count === 1 ? "facility" : "facilities";
  const segments = [`${count} ${facilityWord}`];
  const stateLine = formatNetworkStateList(options.states);
  if (stateLine) segments.push(stateLine);
  const base = segments.join(" · ");
  const idParts: string[] = [];
  const chainId = options.chainId;
  if (chainId != null && String(chainId).trim()) {
    idParts.push(`Chain ID ${String(chainId).trim()}`);
  }
  const networkId = options.networkId;
  if (networkId != null && String(networkId).trim()) {
    idParts.push(`Network ID ${String(networkId).trim()}`);
  }
  if (idParts.length) {
    return `${base} — ${idParts.join(" · ")}`;
  }
  return base;
}

export function formatNetworkPortfolioSubtitle(
  facilityCount: number | null | undefined,
  asOfDate: string | null | undefined,
): string {
  const count = Math.max(0, facilityCount ?? 0);
  const facilityWord = count === 1 ? "facility" : "facilities";
  let line =
    "Portfolio-wide compliance, quality, and financial-performance view " +
    `across all ${count} ${facilityWord}`;
  const period = formatNetworkPortfolioPeriodEnding(asOfDate);
  if (period) {
    line += ` for period ending ${period}`;
  }
  return line;
}
